// Hook per connessione Socket.io e subscribe/unsubscribe ai canali
import { useEffect, useCallback, useRef } from 'react'
import type { Socket } from 'socket.io-client'
import { getSocket, SOCKET_EVENTS } from '../lib/socket'
import { useRealtimeStore } from '../stores/realtime.store'
import { useNotificationsStore } from '../stores/notifications.store'
import type { TechnicianPosition } from '../stores/realtime.store'


export function useRealtime() {
  const socketRef = useRef<Socket | null>(null)
  const { setConnectionStatus, updateTechnicianPosition, addEvent, setTechnicianCheckedOut } =
    useRealtimeStore()
  const { addNotification } = useNotificationsStore()

  useEffect(() => {
    const socket = getSocket()
    socketRef.current = socket

    setConnectionStatus('connecting')

    socket.on('connect', () => {
      setConnectionStatus('connected')
    })

    socket.on('disconnect', () => {
      setConnectionStatus('disconnected')
    })

    socket.on('connect_error', () => {
      setConnectionStatus('error')
    })

    // Aggiornamento posizione tecnico
    socket.on(SOCKET_EVENTS.GEOLOCATION_UPDATE, (data: TechnicianPosition) => {
      updateTechnicianPosition(data)
      addEvent({ id: crypto.randomUUID(), type: SOCKET_EVENTS.GEOLOCATION_UPDATE, payload: data, timestamp: new Date().toISOString() })
    })

    // Nuova notifica
    socket.on(SOCKET_EVENTS.NOTIFICATION_NEW, (data: { id: string; title: string; message: string; type: 'info' | 'success' | 'warning' | 'error'; link?: string }) => {
      addNotification({
        id: data.id,
        type: data.type,
        title: data.title,
        message: data.message,
        link: data.link,
        createdAt: new Date().toISOString(),
      })
    })

    // Check-in / Check-out tecnici
    socket.on(SOCKET_EVENTS.TECHNICIAN_CHECKIN, (data: TechnicianPosition) => {
      updateTechnicianPosition({ ...data, isCheckedIn: true })
    })

    socket.on(SOCKET_EVENTS.TECHNICIAN_CHECKOUT, (data: { userId: string }) => {
      setTechnicianCheckedOut(data.userId)
    })

    // SLA Breach — genera notifica urgente
    socket.on(SOCKET_EVENTS.SLA_BREACH, (data: { ticketId: string; ticketNumber: string }) => {
      addNotification({
        id: crypto.randomUUID(),
        type: 'error',
        title: 'SLA violato',
        message: `Ticket ${data.ticketNumber} ha superato la scadenza SLA`,
        createdAt: new Date().toISOString(),
        link: `/tickets/${data.ticketId}`,
      })
    })

    return () => {
      socket.off('connect')
      socket.off('disconnect')
      socket.off('connect_error')
      socket.off(SOCKET_EVENTS.GEOLOCATION_UPDATE)
      socket.off(SOCKET_EVENTS.NOTIFICATION_NEW)
      socket.off(SOCKET_EVENTS.TECHNICIAN_CHECKIN)
      socket.off(SOCKET_EVENTS.TECHNICIAN_CHECKOUT)
      socket.off(SOCKET_EVENTS.SLA_BREACH)
    }
  }, [setConnectionStatus, updateTechnicianPosition, addEvent, addNotification, setTechnicianCheckedOut])

  const subscribe = useCallback((event: string, handler: (data: unknown) => void) => {
    socketRef.current?.on(event, handler)
    return () => {
      socketRef.current?.off(event, handler)
    }
  }, [])

  const emit = useCallback((event: string, data?: unknown) => {
    socketRef.current?.emit(event, data)
  }, [])

  const joinRoom = useCallback((room: string) => {
    socketRef.current?.emit('join:room', { room })
  }, [])

  const leaveRoom = useCallback((room: string) => {
    socketRef.current?.emit('leave:room', { room })
  }, [])

  return {
    connectionStatus: useRealtimeStore((s) => s.connectionStatus),
    subscribe,
    emit,
    joinRoom,
    leaveRoom,
  }
}
